import React from 'react'
import { ElementProps } from '../base/element'
import { Item } from '../base/item'
import { defStyleChildren } from '../helpers/utils'

import {
  IComponent,
  IContainerComponent,
  IDefaultStyles,
  IStyles
} from '../schemamanager/types'
import { ITabComponent } from './tab'

export interface ITabsComponent extends IContainerComponent {
  type: 'tabs'
  selectedTabIndex?: number
  styleHeader?: IStyles
  styleLabel?: IStyles
  styleIcon?: IStyles
  styleChildren?: IStyles
}

export const Tabs = (props: ElementProps) => {
  // const { sm, children, themeColor, events, disabled } = props
  const { sm, themeColor } = props
  const comp = props.comp as ITabsComponent

  const style: IDefaultStyles = {
    main: {
      classes: {
        margin: 'my-2'
      }
    },
    styleIcon: {
      classes: {
        height: 'h-6',
        width: 'w-6',
        textColor: themeColor().textColor,
        fontWeight: 'font-bold'
      }
    },
    styleHeader: {
      classes: {
        padding: 'px-8 py-5',
        borderWidth: 'border-b',
        flex: 'flex',
        alignItems: 'items-center'
      }
    },
    styleButton: {
      classes: {
        flex: 'inline-flex',
        alignItems: 'items-center',
        padding: 'py-2 px-4',
        backgroundColor: 'bg-white',
        textColor: themeColor().textColor,
        cursor: 'cursor-pointer',
        hover: {
          backgroundColor: themeColor().hoverBgColor
        },
        focus: {
          outline: 'focus:outline-none'
        }
      }
    },
    styleButtonActive: {
      classes: {}
    },

    styleLabel: {
      classes: {
        margin: 'mr-2',
        textColor: 'text-grey-800',
        fontSize: 'text-xl'
      }
    },
    ...defStyleChildren()
  }

  style.styleButtonActive = {
    classes: {
      ...style.styleButton.classes,
      backgroundColor: themeColor().bgColorLight,
      borderWidth: 'border-l border-t border-r',
      borderRadius: 'rounded-t',
      fontWeight: 'font-bold'
    },
    class: style.styleButton.class
  }

  const m = props.mergeStyles(style)
  const selectedTabInd = comp.selectedTabIndex || 0

  const tabDisabled = (tab: IComponent): boolean => {
    const parentDisabled = sm.IsCompDisabled(comp)
    const disabled = sm.IsCompDisabled(tab)
    if (parentDisabled) return true
    return disabled
  }

  const onTabClick = (ind: number) => () => {
    comp.selectedTabIndex = ind
    sm.RenderComponent(comp)
    if (comp.onChange) {
      comp.onChange({ sm, comp, value: ind })
    }
  }

  const getButton = (c: IComponent, ind: number) => {
    const tab = c as ITabComponent
    const disabled = tabDisabled(tab)
    const styleButton =
      selectedTabInd === ind && !disabled ? m.styleButtonActive : m.styleButton
    if (tab.label && tab.iconComp) {
      m.styleLabel.className = m.styleLabel.className + ' ml-2'
    }
    if (disabled) {
      styleButton.className = styleButton.className + ' disabled:opacity-50'
    }

    return (
      <button
        key={sm.getObjectId(tab)}
        {...styleButton}
        type='button'
        onClick={onTabClick(ind)}
        disabled={disabled}
      >
        {tab.iconComp && <tab.iconComp {...m.styleIcon} />}
        {tab.label && <span {...m.styleLabel}>{tab.label}</span>}
      </button>
    )
  }

  const getSelectedTab = () => {
    if (comp.children.length <= selectedTabInd) return null
    const tab = comp.children[selectedTabInd] as ITabComponent
    if (sm.getPropValue(tab, 'hidden')) return null
    return (
      <div {...m.styleChildren}>
        <Item sm={sm} comp={tab} />
      </div>
    )
  }

  return (
    <div {...m.main} {...props.events()}>
      <div {...m.styleHeader}>
        {comp.children.map((c, ind) => getButton(c, ind))}
      </div>
      {getSelectedTab()}
    </div>
  )
}
